export type ChartType = 'bar' | 'line' | 'pie' | 'scatter'

export interface ChartAxisConfig {
  label?: string
  data: string[]
}

export interface ChartPoint {
  x: number
  y: number
}

export interface ChartSeriesConfig {
  label: string
  data: number[] | ChartPoint[]
  color?: string
}

export function isPointSeries(data: ChartSeriesConfig['data']): data is ChartPoint[] {
  return data.length > 0 && typeof data[0] === 'object'
}

export interface ChartPieItem {
  label: string
  value: number
  color?: string
}

export interface AssistantChartData {
  chartType: ChartType
  title?: string
  xAxis?: ChartAxisConfig
  yAxis?: { label?: string; allowDecimals?: boolean }
  series?: ChartSeriesConfig[]
  data?: ChartPieItem[]
}

export interface AssistantTableData {
  title?: string
  columns: string[]
  rows: (string | number)[][]
}

const CHART_CODE_SELECTOR = 'pre > code[class*="chart"]'
const TABLE_CODE_SELECTOR = 'pre > code[class*="datatable"]'

export interface ExtractChartResult {
  cleanedHtml: string
  chart: AssistantChartData | null
  isChartPending: boolean
  table: AssistantTableData | null
  isTablePending: boolean
}

function extractCodeBlock(doc: Document, selector: string): { jsonStr: string; found: boolean } {
  const codeEl = doc.querySelector(selector)
  if (!codeEl) return { jsonStr: '', found: false }
  const jsonStr = (codeEl.textContent ?? '').trim()
  codeEl.parentElement?.remove()
  return { jsonStr, found: true }
}

export function extractChart(html: string): ExtractChartResult {
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const { jsonStr: chartJson, found: hasChart } = extractCodeBlock(doc, CHART_CODE_SELECTOR)
  const { jsonStr: tableJson, found: hasTable } = extractCodeBlock(doc, TABLE_CODE_SELECTOR)

  const cleanedHtml = doc.body.innerHTML

  let chart: AssistantChartData | null = null
  let isChartPending = false
  if (hasChart) {
    try {
      chart = JSON.parse(chartJson) as AssistantChartData
    } catch {
      isChartPending = true
    }
  }

  let table: AssistantTableData | null = null
  let isTablePending = false
  if (hasTable) {
    try {
      table = JSON.parse(tableJson) as AssistantTableData
    } catch {
      isTablePending = true
    }
  }

  return { cleanedHtml, chart, isChartPending, table, isTablePending }
}
